import React, { memo, useContext, useEffect, useReducer } from 'react';
import { useSnackbar } from 'notistack';
import { useParams, Redirect } from 'react-router-dom';
import { isValidObject, isValidString } from '../../../catalog/Validity';
import { context } from '../../../globalState/GlobalState';
import { createCustomTitleTag, createCustomDescriptionTag, createCustomKeywordsTag } from '../uiElements/helmet/MetaTags';
import TitleAndDescriptionTags from '../uiElements/helmet/TitleAndDescriptionTags';
import { fetchFormAPI, fetchFormTitleAPI } from '../../apiCalls/FormAPICalls';
import Form1 from '../../designs/form1/Form1';
import { FormType } from '../../../catalog/enums/FormType';
import { sendWebPageAnalytics } from '../../../catalog/Analytics';

const initialState = {
    title: '',
    published: true,
    formType: FormType.NONE,
    isCustom: false,
    formItems: [],
    formPaymentItems: [],
    loadingSpinner: true,
    secondaryLoadingSpinner: true,
    error: false,
};

function reducer(state, action) {
    switch (action.type) {
        case 'fetchFormTitle':
            return { ...state, title: action.title };
        case 'fetchForm':
            return { ...state, title: action.title, formType: action.formType, published: action.published, isCustom: action.isCustom, formItems: action.content, formPaymentItems: action.paymentContent };
        case 'setLoadingSpinner':
            return { ...state, loadingSpinner: action.loadingSpinner };
        case 'setSecondaryLoadingSpinner':
            return { ...state, secondaryLoadingSpinner: action.loadingSpinner };
        case 'setError':
            return { ...state, error: true };
        default:
            return initialState;
    }
}

const Form = () => {
    const { globalState } = useContext(context);
    const params = useParams();
    const [state, setState] = useReducer(reducer, initialState);
    const { enqueueSnackbar } = useSnackbar();

    useEffect(() => {
        sendWebPageAnalytics(globalState, state.title);
    }, [globalState, state.title]);

    useEffect(() => {
        async function fetchForm() {
            if (isValidString(globalState.accountId) && isValidString(params.formid)) {
                setState({ type: 'setLoadingSpinner', loadingSpinner: true });
                setState({ type: 'setSecondaryLoadingSpinner', loadingSpinner: true });
                await fetchFormTitleAPI(globalState.accountId, params.formid, setState, enqueueSnackbar);
                setState({ type: 'setLoadingSpinner', loadingSpinner: false });
                await fetchFormAPI(globalState.organizationName, globalState.teamName, globalState.accountId, globalState.teamAlias, params.formid, setState, enqueueSnackbar);
                setState({ type: 'setSecondaryLoadingSpinner', loadingSpinner: false });
            }
        }

        fetchForm();
    }, [globalState.accountId, params.formid]);

    const getFormDesign = () => {
        if (!isValidObject(globalState) || !isValidObject(globalState.websiteStyles)) {
            return null;
        }

        return (
            <Form1
                title={state.title}
                formItems={state.formItems}
                formType={state.formType}
                formPaymentItems={state.formPaymentItems}
                published={state.published}
                loadingSpinner={state.loadingSpinner}
                secondaryLoadingSpinner={state.secondaryLoadingSpinner}
            />
        );
    };

    if (state.error) {
        return <Redirect to="/notfound" />;
    }

    return (
        <>
            {isValidString(state.title) ? (
                <TitleAndDescriptionTags
                    title={createCustomTitleTag(state.title, globalState)}
                    pageTitle={state.title}
                    description={createCustomDescriptionTag(`Check out our ${state.title}`, globalState)}
                    keywords={createCustomKeywordsTag(`${state.title}, Form`, globalState)}
                />
            ) : null}
            {getFormDesign()}
        </>
    );
};

export default memo(Form);
